import { useQuery } from '@tanstack/react-query';
import React from 'react';
import type { ConnectedOpportunity } from '../../../js/api/models/ConnectedOpportunity';
import { HealthcareEntitiesService } from '../../../js/api/services/HealthcareEntitiesService';
import { newLineToBr } from '../../../js/utils/string';
import {
  FACILITY_QUERY_KEY,
  FACILITY_SPECIALTIES_QUERY_KEY,
} from '../pages/Organization/Facilities/queryKeys';
import { formatApiDate } from '../utils/date';

const DATE_FORMAT = 'LLL d, yyyy';

type FacilityDetailsProps = {
  showDetails: boolean;
  opportunity?: ConnectedOpportunity;
  facilityId?: string;
  codeDefinitions?: Record<string, Record<string, string>>;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function getDisplayValueForField(field: any) {
  let displayValue = field.field_value;

  if (field.field_type === 'boolean' && typeof field.field_value === 'boolean') {
    return field.field_value ? 'Yes' : 'No';
  }

  if (field.field_type === 'number' && field.field_value != null) {
    return Number(field.field_value).toLocaleString();
  }

  if (field.field_type === 'multiselect' && Array.isArray(field.field_value)) {
    if (Array.isArray(field.choices) && field.choices.length > 0) {
      displayValue = field.field_value
        .map((val: string) => {
          const match = field.choices.find((choice: any) => choice.code === val);
          return match ? match.title : val;
        })
        .join(', ');
    } else {
      displayValue = field.field_value.join(', ');
    }
  }

  return displayValue;
}

export function FacilityDetails({
  showDetails,
  opportunity,
  facilityId,
  codeDefinitions,
}: FacilityDetailsProps) {
  const { data: detailedFacility } = useQuery({
    queryKey: [FACILITY_QUERY_KEY, facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesRetrieve({ id: facilityId! }),
    enabled: !!facilityId && showDetails,
  });

  const { data: facilitySpecialties } = useQuery({
    queryKey: [FACILITY_SPECIALTIES_QUERY_KEY, facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesList({
        facility: facilityId!,
      }),
    enabled: !!facilityId && showDetails,
  });

  if (!showDetails) return null;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const renderFields = () => {
    const allFields: Array<{ field_name: string; field_title: string; field_value: any }> = [];

    if (detailedFacility?.emr) {
      allFields.push({
        field_name: 'emr',
        field_title: 'EMR',
        field_value: detailedFacility.emr,
      });
    }

    if (detailedFacility?.facility_type_fields) {
      try {
        const fields =
          typeof detailedFacility.facility_type_fields === 'string'
            ? JSON.parse(detailedFacility.facility_type_fields)
            : detailedFacility.facility_type_fields;

        const filteredFields = fields.filter(
          (field: any) => field.show_on_connection !== false && field.field_value
        );

        if (Array.isArray(filteredFields) && filteredFields.length > 0) {
          filteredFields.forEach((field: any) => {
            allFields.push({
              field_name: field.field_name,
              field_title: field.field_title,
              field_value: field.field_unit
                ? `${getDisplayValueForField(field)} ${field.field_unit}`
                : getDisplayValueForField(field),
            });
          });
        }
      } catch (error) {
        console.error('Error parsing facility_type_fields:', error);
      }
    }

    if (allFields.length >= 6) {
      const midPoint = Math.ceil(allFields.length / 2);
      const leftColumnFields = allFields.slice(0, midPoint);
      const rightColumnFields = allFields.slice(midPoint);

      return (
        <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-4">
          <div className="tw:w-full tw:md:w-[75%]">
            {leftColumnFields.map((field) => (
              <div key={field.field_name} className="tw:flex tw:justify-between tw:mb-1 tw:text-sm">
                <span className="tw:font-semibold">{field.field_title}:</span>
                <span className="tw:text-right">{field.field_value}</span>
              </div>
            ))}
          </div>
          <div className="tw:w-full tw:md:w-[75%]">
            {rightColumnFields.map((field) => (
              <div key={field.field_name} className="tw:flex tw:justify-between tw:mb-1 tw:text-sm">
                <span className="tw:font-semibold">{field.field_title}:</span>
                <span className="tw:text-right">{field.field_value}</span>
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      return (
        <div className="tw:w-full tw:md:w-[75%]">
          {allFields.map((field) => (
            <div key={field.field_name} className="tw:flex tw:justify-between tw:mb-1 tw:text-sm">
              <span className="tw:font-semibold">{field.field_title}:</span>
              <span className="tw:text-right">{field.field_value}</span>
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <div className="tw:text-zinc-500 tw:mt-2 tw:border tw:border-zinc-300 tw:dark:border-zinc-600 tw:p-2 tw:rounded-lg">
      <div className="tw:mt-2">
        <h6 className="tw:mb-2 tw:text-base tw:font-semibold">
          {detailedFacility?.facility_type && (
            <>
              {codeDefinitions?.FacilityTypes?.[detailedFacility?.facility_type] ||
                detailedFacility?.facility_type}{' '}
            </>
          )}
          Facility
        </h6>
        {renderFields()}

        {facilitySpecialties?.results &&
          Array.isArray(facilitySpecialties.results) &&
          facilitySpecialties.results.length > 0 &&
          opportunity?.specialty &&
          facilitySpecialties.results.some(
            (specialty: any) => specialty.specialty === opportunity.specialty
          ) && (
            <div className="tw:mt-3 tw:pt-3 tw:border-t tw:border-zinc-300 tw:dark:border-zinc-600">
              {facilitySpecialties.results
                .filter((specialty: any) => specialty.specialty === opportunity.specialty)
                .map((specialty: any) => (
                  <div key={specialty.id} className="tw:mb-3">
                    <h6 className="tw:mb-2 tw:text-base tw:font-semibold">
                      {codeDefinitions?.Specialty?.[specialty.specialty] || specialty.specialty}
                    </h6>
                    {specialty.dynamic_fields &&
                      (() => {
                        try {
                          const fields =
                            typeof specialty.dynamic_fields === 'string'
                              ? JSON.parse(specialty.dynamic_fields)
                              : specialty.dynamic_fields;

                          const filteredFields = fields.filter(
                            (field: any) => field.show_on_connection !== false && field.field_value
                          );

                          if (Array.isArray(filteredFields) && filteredFields.length > 0) {
                            if (filteredFields.length >= 6) {
                              const midPoint = Math.ceil(filteredFields.length / 2);
                              const leftColumnFields = filteredFields.slice(0, midPoint);
                              const rightColumnFields = filteredFields.slice(midPoint);

                              return (
                                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-4">
                                  <div className="tw:w-full tw:md:w-[75%]">
                                    {leftColumnFields.map((field: any) => (
                                      <div
                                        key={field.field_name}
                                        className="tw:flex tw:justify-between tw:mb-1 tw:text-sm"
                                      >
                                        <span className="tw:font-semibold">
                                          {field.field_title}:
                                        </span>
                                        <span className="tw:text-right">
                                          {getDisplayValueForField(field)}
                                          {field.field_unit && ` ${field.field_unit}`}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="tw:w-full tw:md:w-[75%]">
                                    {rightColumnFields.map((field: any) => (
                                      <div
                                        key={field.field_name}
                                        className="tw:flex tw:justify-between tw:mb-1 tw:text-sm"
                                      >
                                        <span className="tw:font-semibold">
                                          {field.field_title}:
                                        </span>
                                        <span className="tw:text-right">
                                          {getDisplayValueForField(field)}
                                          {field.field_unit && ` ${field.field_unit}`}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div className="tw:w-full tw:md:w-[75%]">
                                  {filteredFields.map((field: any) => (
                                    <div
                                      key={field.field_name}
                                      className="tw:flex tw:justify-between tw:mb-1 tw:text-sm"
                                    >
                                      <span className="tw:font-semibold">{field.field_title}:</span>
                                      <span className="tw:text-right">
                                        {getDisplayValueForField(field)}
                                        {field.field_unit && ` ${field.field_unit}`}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                          }
                          return null;
                        } catch (error) {
                          console.error('Error parsing specialty dynamic_fields:', error);
                          return null;
                        }
                      })()}
                  </div>
                ))}
            </div>
          )}
      </div>

      {opportunity && (
        <div className="tw:mt-3 tw:pt-3 tw:border-t tw:border-zinc-300 tw:dark:border-zinc-600">
          <h6 className="tw:mb-2 tw:text-base tw:font-semibold">Opportunity Specifics</h6>
          {opportunity.starts_on && (
            <div className="tw:mb-1 tw:text-sm">
              <span className="tw:font-semibold">Start Date: </span>
              <span className="tw:text-zinc-500">
                {formatApiDate(opportunity.starts_on, { dateFormat: DATE_FORMAT })}
              </span>
            </div>
          )}
          {opportunity.estimated_duration && (
            <div className="tw:mb-1 tw:text-sm">
              <span className="tw:font-semibold">Duration: </span>
              <span className="tw:text-right">{opportunity.estimated_duration}</span>
            </div>
          )}
          {opportunity.description && (
            <div className="tw:mb-1 tw:text-sm">
              <span className="tw:font-semibold">Description:</span>
              <div className="tw:mt-1">{opportunity.description}</div>
            </div>
          )}
          {opportunity.additional_info && (
            <div className="tw:mb-1 tw:text-sm">
              <span className="tw:font-semibold">Additional Info:</span>
              <div
                className="tw:mt-1"
                dangerouslySetInnerHTML={{
                  __html: newLineToBr(opportunity.additional_info),
                }}
              />
            </div>
          )}
        </div>
      )}
      {detailedFacility?.extra_info && (
        <div className="tw:mt-3 tw:pt-3 tw:border-t tw:border-zinc-300 tw:dark:border-zinc-600">
          <div
            className="tw:text-sm"
            dangerouslySetInnerHTML={{
              __html: newLineToBr(detailedFacility?.extra_info || ''),
            }}
          />
        </div>
      )}
    </div>
  );
}
